import { useEffect, useRef, useState } from 'react'
import config from 'src/config'
import { post } from 'src/functions/fetch'
import {
  isProductRecommendations,
  type tProductRecommendations,
} from 'src/shared/frontendTypes/productRecommendations'

// Recommendations belong to this page only. Do not cache mutable prices or
// replace the main product object when they arrive (that resets its selection).
export const useProductRecommendations = (
  productId: number,
  selectedOptions: number[],
  enabled: boolean,
) => {
  const [attempt, setAttempt] = useState(0)
  const [result, setResult] = useState<{
    key: string
    data?: tProductRecommendations
    error?: boolean
  }>()
  const optionsRef = useRef(selectedOptions)
  optionsRef.current = selectedOptions
  const key = `${config.shop.shop}:${config.shop.lang}:${productId}:${attempt}`

  useEffect(() => {
    if (!enabled) return
    const controller = new AbortController()
    const options = Array.from(new Set(optionsRef.current)).sort((a, b) => a - b)
    const selectionKey = options.join('-')

    void post({
      url: `product/${productId}/recommendations`,
      body: { ...config.shop, selectedOptions: options },
      signal: controller.signal,
    }).then(data => {
      if (controller.signal.aborted) return
      // A response for a different selection must not seed the series cache.
      if (!isProductRecommendations(data) || data.dynamicSerie?.selectionKey !== selectionKey) {
        setResult({ key, error: true })
        return
      }
      setResult({ key, data })
    }).catch(() => {
      if (!controller.signal.aborted) setResult({ key, error: true })
    })

    return () => controller.abort()
  }, [enabled, key, productId])

  const current = enabled && result?.key === key ? result : undefined
  return {
    data: current?.data,
    loading: enabled && !current,
    error: Boolean(current?.error),
    retry: () => setAttempt(value => value + 1),
  }
}
